import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useStore } from '@/shared/hooks/useStore';
import { Input } from '@shared/components/common/Input';
import Table from '@shared/components/table/Table';
import { ColumnDef } from '@tanstack/react-table';
import Pagination from '@shared/components/pagination/Pagination';
import { ADMIN_ROUTES } from '@/shared/configs/constants';
import IconImages from '@/shared/assets/images/icons';
import Tooltip from '@shared/components/common/Tooltip';

export default observer(function UserManagementPage() {
    // hooks
    const navigate = useNavigate();
    const { groupAdminStore } = useStore();

    // Search States
    const [searchIdName, setSearchIdName] = useState('');
    const [searchEmail, setSearchEmail] = useState('');
    const [searchPhone, setSearchPhone] = useState('');
    const [searchPharmacistReg, setSearchPharmacistReg] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const fetchUsers = (page: number) => {
        const params = {
            page,
            size: pageSize,
            name: searchIdName.trim() || undefined,
            email: searchEmail.trim() || undefined,
            phoneNumber: searchPhone.trim() || undefined,
            pharmacistNumber: searchPharmacistReg.trim() || undefined
        };
        groupAdminStore.fetchUsers(params);
    };

    useEffect(() => {
        fetchUsers(currentPage);
    }, [currentPage]);

    // Search handler
    const handleSearch = () => {
        if (currentPage === 1) {
            fetchUsers(1);
        } else {
            setCurrentPage(1);
        }
    };

    // Page change handler
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Table Columns
    const columns: ColumnDef<any>[] = useMemo(() => [
        {
            accessorKey: 'no',
            header: 'No.',
            cell: ({ row }) => (currentPage - 1) * pageSize + row.index + 1,
            size: 60,
            enableSorting: false
        },
        {
            accessorKey: 'userId',
            header: 'ユーザーID',
            cell: ({ row }) => row.original.userId || '--',
            enableSorting: false
        },
        {
            accessorKey: 'fullname',
            header: '氏名',
            size: 240,
            cell: ({ row }) => (
                <div className='flex flex-col gap-[10px]'>
                    <Tooltip content={`${row.original.firstNameFuri || ''} ${row.original.lastNameFuri || ''}`} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                        <div className='line-clamp-2'>{`${row.original.firstNameFuri || ''} ${row.original.lastNameFuri || ''}`}</div>
                    </Tooltip>
                    <Tooltip content={`${row.original.firstName || ''} ${row.original.lastName || ''}`} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                        <div className='text-[#6F767E] line-clamp-2'>{`${row.original.firstName || ''} ${row.original.lastName || ''}`}</div>
                    </Tooltip>
                </div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'email',
            header: 'メールアドレス',
            size: 240,
            cell: ({ row }) => (
                <Tooltip content={row.original.email || '--'} triggerAsChild contentClassName='max-w-[320px] whitespace-pre-line text-break'>
                    <div className='line-clamp-2'>{row.original.email || '--'}</div>
                </Tooltip>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'phoneNumber',
            header: '電話番号',
            cell: ({ row }) => row.original.phoneNumber || '--',
            enableSorting: false
        },
        {
            accessorKey: 'pharmacistNumber',
            header: '薬剤師名簿登録番号',
            cell: ({ row }) => row.original.pharmacistNumber || '--',
            enableSorting: false
        },
        {
            accessorKey: 'viewableItemCount',
            header: '閲覧可能商品数',
            enableSorting: false
        },
        {
            id: 'actions',
            header: '操作',
            cell: ({ row }) => (
                <div className='flex justify-center'>
                    <button
                        className='border border-primary-light rounded-[4px] transition-colors'
                        onClick={() => navigate(`${ADMIN_ROUTES.userManagement.href}/${row.original.id}`)}
                    >
                        <img src={IconImages.userCircleIc} alt='user-circle' className='w-5 h-5' />
                    </button>
                </div>
            ),
            size: 80,
            enableSorting: false
        }
    ], [currentPage]);

    return (
        <section className='py-6'>
            {/* Page Title */}
            <h1 className='text-[30px] leading-[36px] font-semibold mb-10'>
                ユーザー管理
            </h1>

            {/* Search Card */}
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-4 md:p-6 mb-6'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                    {/* User ID / Name */}
                    <div>
                        <label className='block leading-[21px] mb-2'>
                            ユーザーID / 氏名
                        </label>
                        <Input
                            type='text'
                            value={searchIdName}
                            onChange={e => setSearchIdName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            className='px-4 leading-[21px] rounded-[0px] h-[56px] border border-[#E7E7E7] w-full'
                        />
                    </div>

                    {/* Email */}
                    <div>
                        <label className='block leading-[21px] mb-2'>
                            メールアドレス
                        </label>
                        <Input
                            type='text'
                            value={searchEmail}
                            onChange={e => setSearchEmail(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            className='px-4 leading-[21px] rounded-[0px] h-[56px] border border-[#E7E7E7] w-full'
                        />
                    </div>

                    {/* Phone */}
                    <div>
                        <label className='block leading-[21px] mb-2'>
                            電話番号
                        </label>
                        <Input
                            type='text'
                            value={searchPhone}
                            onChange={e => setSearchPhone(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            className='px-4 leading-[21px] rounded-[0px] h-[56px] border border-[#E7E7E7] w-full'
                        />
                    </div>

                    {/* Pharmacist Reg Number */}
                    <div>
                        <label className='block leading-[21px] mb-2'>
                            薬剤師名簿登録番号
                        </label>
                        <Input
                            type='text'
                            value={searchPharmacistReg}
                            onChange={e => setSearchPharmacistReg(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            className='px-4 leading-[21px] rounded-[0px] h-[56px] border border-[#E7E7E7] w-full'
                        />
                    </div>
                </div>

                {/* Search Button */}
                <div className='flex justify-center'>
                    <button
                        onClick={handleSearch}
                        className='bg-primary rounded-[10px] text-white w-full md:w-[291px] h-[56px] font-semibold transition-colors'
                    >
                        検索
                    </button>
                </div>
            </div>

            {/* Data Table */}
            <div className='bg-white rounded-[10px] shadow-[0px_0px_14px_0px_#00000008] p-4 md:p-6'>
                <Table
                    columns={columns}
                    data={groupAdminStore.users}
                    showTableFooter={false}
                    showSelectPageSize={false}
                    noData={<p className='text-[#999999]'>該当するデータが見つかりませんでした。</p>}
                    headerClassName='bg-[#F2F2F2] text-[#6F767E] font-medium'
                />

                <div className='flex justify-center py-4 border-t border-gray-200 mt-4'>
                    <Pagination
                        type='table-pagination'
                        paginationClassName='w-fit'
                        currentPage={currentPage}
                        totalRecords={groupAdminStore.totalUsers}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                    />

                </div>
            </div>
        </section>
    );
});
